import { useEffect, useRef } from "react";
import { ShieldAlert } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";

interface EnforceSSOConfirmDialogProps {
  domains: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

export function EnforceSSOConfirmDialog({
  domains,
  onConfirm,
  onCancel,
}: EnforceSSOConfirmDialogProps) {
  const { t } = useLocale();
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  // Move focus into the dialog on open; restore Esc → cancel.
  useEffect(() => {
    confirmRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/40"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="sso-enforce-title"
        aria-describedby="sso-enforce-body"
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-xl"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-warning/10 p-2 text-warning">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3
              id="sso-enforce-title"
              className="text-lg font-semibold text-foreground"
            >
              {t("sso.confirm.enforceTitle")}
            </h3>
            <p
              id="sso-enforce-body"
              className="mt-1 text-sm text-muted-foreground"
            >
              {t("sso.confirm.enforceBody", { domains: domains.join(", ") })}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t("common.cancel")}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t("sso.confirm.enforceConfirm")}
          </button>
        </div>
      </div>
    </>
  );
}
